import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Minus, ArrowRight, Crown, Briefcase, Globe, BadgeCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { PLAN_TIERS, FEATURE_MATRIX, PARTNER_TIERS } from '../config/plans';

const Cell = ({ value }) => {
  if (value === true) return <Check size={16} className="text-gold mx-auto" data-testid="cell-yes" />;
  if (value === false) return <Minus size={14} className="text-muted-foreground/40 mx-auto" data-testid="cell-no" />;
  return <span className="text-xs text-zinc-200" data-testid="cell-text">{value}</span>;
};

export default function Plans() {
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-[#1A1A1A] bg-[#080808]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="plans-back">
            <BadgeCheck size={18} className="text-gold" />
            <span className="font-serif text-lg">Assagart</span>
          </Link>
          <Link to="/login" className="text-xs text-muted-foreground hover:text-gold uppercase tracking-[0.2em]">Sign in</Link>
        </div>
      </header>

      <section className="text-center px-6 lg:px-12 py-20 max-w-3xl mx-auto">
        <p className="text-gold uppercase tracking-[0.3em] text-xs mb-4">Pricing</p>
        <h1 className="font-serif text-5xl lg:text-6xl text-white mb-6 leading-tight">
          One vault. Three ways to use it.
        </h1>
        <p className="text-base text-muted-foreground leading-relaxed">
          Start free, upgrade when you outgrow it, and add your advisors when the workflow does.
          No hidden seat fees. Cancel anytime.
        </p>
      </section>

      <section className="px-6 lg:px-12 max-w-6xl mx-auto" data-testid="plans-tier-cards">
        <div className="grid md:grid-cols-3 gap-4">
          {PLAN_TIERS.map(tier => (
            <div
              key={tier.id}
              data-testid={`tier-${tier.id}`}
              className={`luxury-card p-6 flex flex-col ${tier.highlight ? 'border-gold/40 shadow-[0_0_40px_rgba(212,175,55,0.08)]' : ''}`}
            >
              {tier.highlight && (
                <div className="inline-block self-start text-[10px] uppercase tracking-[0.25em] text-gold bg-gold/10 border border-gold/30 px-2 py-0.5 rounded mb-3">
                  Most popular
                </div>
              )}
              <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">{tier.name}</p>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="font-serif text-5xl text-white">{tier.price}</span>
                <span className="text-xs text-muted-foreground">{tier.cadence}</span>
              </div>
              <p className="text-sm text-gold/80 italic mb-3">{tier.promise}</p>
              <p className="text-xs text-muted-foreground leading-relaxed mb-6 flex-1">{tier.summary}</p>
              <Link to={tier.cta.to}>
                <Button
                  className={`w-full ${tier.highlight ? 'bg-gold text-black hover:bg-amber-300' : 'btn-secondary'}`}
                  data-testid={`tier-cta-${tier.id}`}
                >
                  {tier.cta.label} <ArrowRight size={14} className="ml-2" />
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 lg:px-12 max-w-6xl mx-auto mt-20" data-testid="plans-comparison-table">
        <p className="text-gold uppercase tracking-[0.3em] text-xs text-center mb-3">Side-by-side</p>
        <h2 className="font-serif text-3xl text-white text-center mb-12">Compare every feature.</h2>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[640px]">
            <thead className="sticky top-0 bg-[#050505]">
              <tr>
                <th className="text-left px-4 py-4 text-[10px] uppercase tracking-[0.2em] text-muted-foreground w-1/2">Feature</th>
                {PLAN_TIERS.map(t => (
                  <th
                    key={t.id}
                    className={`text-center px-3 py-4 text-[10px] uppercase tracking-[0.2em] ${t.highlight ? 'text-gold' : 'text-muted-foreground'}`}
                  >
                    {t.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEATURE_MATRIX.map(section => (
                <React.Fragment key={section.section}>
                  <tr className="bg-[#0A0A0A]">
                    <td colSpan={1 + PLAN_TIERS.length} className="px-4 py-3 text-[11px] uppercase tracking-[0.3em] text-gold border-y border-gold/15">
                      {section.section}
                    </td>
                  </tr>
                  {section.rows.map((row, i) => (
                    <tr key={i} className="border-b border-[#1A1A1A]">
                      <td className="px-4 py-3 align-top">
                        <p className="text-sm text-white">{row.feature}</p>
                        {row.sub && <p className="text-[10px] text-muted-foreground mt-0.5 leading-relaxed">{row.sub}</p>}
                      </td>
                      {PLAN_TIERS.map(t => (
                        <td key={t.id} className="px-3 py-3 text-center align-top">
                          <Cell value={row.include[t.id]} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="px-6 lg:px-12 max-w-6xl mx-auto mt-24" data-testid="plans-partner-tiers">
        <p className="text-gold uppercase tracking-[0.3em] text-xs text-center mb-3">For brokers, wealth managers, insurers</p>
        <h2 className="font-serif text-3xl text-white text-center mb-12">Partner with Assagart.</h2>
        <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
          {PARTNER_TIERS.map(t => (
            <div key={t.id} className="luxury-card p-6" data-testid={`partner-tier-${t.id}`}>
              <div className="flex items-center gap-2 mb-3">
                {t.id === 'broker' ? <Briefcase size={14} className="text-gold" /> : <Globe size={14} className="text-gold" />}
                <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{t.name}</p>
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="font-serif text-2xl text-white">{t.price}</span>
                {t.cadence && <span className="text-xs text-muted-foreground">{t.cadence}</span>}
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed mb-5">{t.summary}</p>
              {t.cta.to.startsWith('mailto:') ? (
                <a href={t.cta.to} className="inline-flex items-center gap-1 text-xs text-gold hover:text-amber-300 uppercase tracking-wider">
                  {t.cta.label} <ArrowRight size={12} />
                </a>
              ) : (
                <Link to={t.cta.to} className="inline-flex items-center gap-1 text-xs text-gold hover:text-amber-300 uppercase tracking-wider">
                  {t.cta.label} <ArrowRight size={12} />
                </Link>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-white/5 mt-24 px-6 lg:px-12 py-16">
        <div className="max-w-4xl mx-auto grid sm:grid-cols-3 gap-8 text-center">
          <div>
            <Crown size={20} className="text-gold mx-auto mb-3" />
            <p className="text-sm text-white mb-1">No long contracts</p>
            <p className="text-[11px] text-muted-foreground leading-relaxed">Pay monthly. Cancel anytime. Your data exports in one click.</p>
          </div>
          <div>
            <BadgeCheck size={20} className="text-gold mx-auto mb-3" />
            <p className="text-sm text-white mb-1">Bank-grade encryption</p>
            <p className="text-[11px] text-muted-foreground leading-relaxed">Field-level PII encryption. Stripe PCI-DSS Level 1 for payments.</p>
          </div>
          <div>
            <Briefcase size={20} className="text-gold mx-auto mb-3" />
            <p className="text-sm text-white mb-1">FCA-aligned network</p>
            <p className="text-[11px] text-muted-foreground leading-relaxed">Every verified partner is cross-referenced against the FCA register.</p>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#1A1A1A] py-10 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-xs text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} Assagart. Per Illos Qui Perdurant.</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-gold">Privacy</Link>
            <Link to="/terms" className="hover:text-gold">Terms</Link>
            <a href="mailto:hello@assagart.co.uk" className="hover:text-gold">Contact</a>
            <Link to="/brokers/apply" className="hover:text-gold">For Brokers</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
